const MINUTES_IN_DAY = 24 * 60;
const MINUTES_IN_WEEK = 7 * MINUTES_IN_DAY;

export const normalizeOpeningPeriods = (openingHours) => {
  const periods = openingHours?.periods;
  if (!Array.isArray(periods)) return [];

  return periods.flatMap((period) => {
    if (!period || period.isClosed) return [];

    if (Number.isInteger(period.day)) {
      const openMinutes = Number(period.open);
      const closeMinutes = Number(period.close);
      if (period.isOpen24Hours) {
        const start = period.day * MINUTES_IN_DAY;
        return [{ day: period.day, start, end: start + MINUTES_IN_DAY, isOpen24Hours: true }];
      }
      if (!Number.isFinite(openMinutes) || !Number.isFinite(closeMinutes)) return [];
      const start = period.day * MINUTES_IN_DAY + openMinutes;
      let end = period.day * MINUTES_IN_DAY + closeMinutes;
      if (end <= start) end += MINUTES_IN_DAY;
      return [{ day: period.day, start, end, isOpen24Hours: false }];
    }

    const open = period.open;
    if (!Number.isInteger(open?.day)) return [];
    const openMinutes = Number(open.hour || 0) * 60 + Number(open.minute || 0);
    const start = open.day * MINUTES_IN_DAY + openMinutes;
    const close = period.close;

    if (!close || !Number.isInteger(close.day)) {
      return [{ day: open.day, start, end: start + MINUTES_IN_DAY, isOpen24Hours: true }];
    }

    const closeMinutes = Number(close.hour || 0) * 60 + Number(close.minute || 0);
    let dayOffset = (close.day - open.day + 7) % 7;
    if (dayOffset === 0 && closeMinutes <= openMinutes) dayOffset = 1;
    const end = open.day * MINUTES_IN_DAY + dayOffset * MINUTES_IN_DAY + closeMinutes;
    return [{
      day: open.day,
      start,
      end,
      isOpen24Hours: end - start >= MINUTES_IN_DAY,
    }];
  });
};

export const formatOpeningTime = (absoluteMinutes) => {
  const minutes = ((absoluteMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

export const getOpeningStatusText = (openingHours, t, now = new Date()) => {
  const periods = normalizeOpeningPeriods(openingHours);
  if (openingHours?.status === 'unknown' || openingHours?.openNow == null || !periods.length) {
    return t('openingHours.unknown');
  }

  const nowInWeek = now.getDay() * MINUTES_IN_DAY + now.getHours() * 60 + now.getMinutes();
  const occurrences = periods.flatMap((period) =>
    [-MINUTES_IN_WEEK, 0, MINUTES_IN_WEEK].map((shift) => ({
      ...period,
      start: period.start + shift,
      end: period.end + shift,
    })));

  if (openingHours?.openNow === true) {
    const current = occurrences
      .filter((period) => period.start <= nowInWeek && nowInWeek < period.end)
      .sort((a, b) => a.end - b.end)[0];
    return current
      ? t('openingHours.openUntil', { time: formatOpeningTime(current.end) })
      : t('openingHours.openNow');
  }

  const next = occurrences
    .filter((period) => period.start > nowInWeek)
    .sort((a, b) => a.start - b.start)[0];
  if (!next) return t('openingHours.closed');

  const day = ((Math.floor(next.start / MINUTES_IN_DAY) % 7) + 7) % 7;
  return t('openingHours.opensAt', {
    day: t(`openingHours.days.${day}`),
    time: formatOpeningTime(next.start),
  });
};
